import { defaultStopTime } from "../components/trip-planner/trip-date";
import type { TripDay, TripStop } from "../types";

/**
 * When a stop happens (traveller tests, 2026-11-04).
 *
 * A stop with no time used to take a slot from its place in the day alone
 * (10:00, 13:30, 17:00), so a balloon flight that leaves at 05:00 showed
 * 13:30 and a restaurant that opens at noon showed 10:00. Times now come from
 * the place itself, read from its catalogue row: a fixed session (the
 * balloon's 05:00) is its own start, and anything else keeps its slot moved
 * inside the place's hours. Nothing is invented: a place we know nothing
 * about keeps the old slot.
 *
 * Pure: web and app both import it.
 */

/** What a place says about its times, from its catalogue detail. */
export interface PlaceHours {
  /** The lead package's window, "HH:MM" or "HH:MM:SS"; equal for a fixed session. */
  readonly timeStart?: string | null;
  readonly timeEnd?: string | null;
  /** Opening hours by weekday ("mon".."sun"), as restaurants carry them. */
  readonly openingHours?: Readonly<Record<string, { readonly open?: string | null; readonly close?: string | null } | null>> | null;
}

const SLOT_MIN = 180;

export function toMin(hhmm: string | null | undefined): number | null {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 24 || min > 59) return null;
  return h * 60 + min;
}

export function fromMin(total: number): string {
  const clamped = Math.max(0, Math.min(23 * 60 + 59, Math.round(total)));
  return `${String(Math.floor(clamped / 60)).padStart(2, "0")}:${String(clamped % 60).padStart(2, "0")}`;
}

const WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

/** The window a place is open on a day, in minutes; close past midnight runs to 24:00. */
function openWindow(hours: PlaceHours, weekday: number | null): { from: number; to: number } | null {
  const oh = hours.openingHours;
  if (oh) {
    const today = weekday !== null ? oh[WEEKDAYS[weekday]!] : null;
    const any = today ?? Object.values(oh).find((d) => d && d.open);
    const from = toMin(any?.open);
    let to = toMin(any?.close);
    if (from !== null) {
      if (to === null || to <= from) to = 24 * 60;
      return { from, to };
    }
  }
  const from = toMin(hours.timeStart);
  const to = toMin(hours.timeEnd);
  if (from !== null && to !== null && to > from) return { from, to };
  return null;
}

/**
 * The time a stop gets when the guest has set none, or null when the place
 * says nothing about its times (the caller keeps the slot).
 */
export function suggestStopTime(
  hours: PlaceHours | null | undefined,
  index: number,
  weekday: number | null = null,
): { startTime: string; endTime: string } | null {
  if (!hours) return null;
  const slot = defaultStopTime(index);
  const slotStart = toMin(slot.startTime)!;
  // A fixed session: the balloon leaves at 05:00 whatever the slot.
  const s = toMin(hours.timeStart);
  const e = toMin(hours.timeEnd);
  if (!hours.openingHours && s !== null && (e === null || e === s)) {
    return { startTime: fromMin(s), endTime: fromMin(s + SLOT_MIN) };
  }
  const win = openWindow(hours, weekday);
  if (!win) return null;
  // Inside the window, leaving at least an hour before it closes.
  const latest = Math.max(win.from, win.to - 60);
  const start = Math.min(Math.max(slotStart, win.from), latest);
  const end = Math.min(start + SLOT_MIN, win.to);
  return { startTime: fromMin(start), endTime: fromMin(end) };
}

/** Every stop's start in minutes: its own, else its slot. */
function startOf(stop: TripStop, index: number): number {
  return toMin(stop.startTime) ?? toMin(defaultStopTime(index).startTime)!;
}

/**
 * Give every stop of a day its time and put them in time order. A stop with
 * no time takes `suggest(stop, index)` when that answers, else its slot.
 * Returns the same day object when nothing changes.
 */
export function timeDay(
  day: TripDay,
  suggest: (stop: TripStop, index: number) => { startTime: string; endTime: string } | null = () => null,
): TripDay {
  let changed = false;
  const timed = day.stops.map((stop, i) => {
    if (stop.startTime) return stop;
    changed = true;
    const t = suggest(stop, i) ?? defaultStopTime(i);
    return { ...stop, startTime: t.startTime, endTime: stop.endTime ?? t.endTime };
  });
  const sorted = timed
    .map((stop, i) => ({ stop, i, at: startOf(stop, i) }))
    .sort((a, b) => a.at - b.at || a.i - b.i)
    .map((x) => x.stop);
  if (!changed && sorted.every((s, i) => s === day.stops[i])) return day;
  return { ...day, stops: sorted };
}

/**
 * The guest set a stop's time: write it, give the day's other stops the
 * times they were showing (so nothing jumps), and sort the day by time.
 * Clearing a time (both undefined) only clears it, as before.
 */
export function retimeStop(
  day: TripDay,
  stopId: string,
  startTime: string | undefined,
  endTime: string | undefined,
): TripDay {
  if (!startTime && !endTime) {
    return {
      ...day,
      stops: day.stops.map((s) => (s.id === stopId ? { ...s, startTime: undefined, endTime: undefined } : s)),
    };
  }
  const shown = timeDay(day);
  // `timeDay` may already have reordered; set the edited stop, then sort again.
  const edited = {
    ...shown,
    stops: shown.stops.map((s) => (s.id === stopId ? { ...s, startTime, endTime } : s)),
  };
  return timeDay(edited);
}

/**
 * Whether an end time cannot follow its start. A late night that runs past
 * midnight (from 18:00 on, ending by 06:00) is fine; anything else ending at
 * or before it starts is not.
 */
export function endBeforeStart(startTime: string | undefined, endTime: string | undefined): boolean {
  const s = toMin(startTime);
  const e = toMin(endTime);
  if (s === null || e === null) return false;
  if (e > s) return false;
  const overnight = s >= 18 * 60 && e <= 6 * 60;
  return !overnight;
}
